import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  Send,
  Smile,
  Paperclip,
  Phone,
  Video,
  MoreVertical,
  Calendar,
  CalendarClock,
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Code2,
  Link2,
  Eye,
  Clock,
  MessageSquare,
  BarChart2,
  CheckSquare,
  HelpCircle,
  Table2,
  X,
  FileIcon,
  Trash2,
  Edit3,
  MoreHorizontal,
} from "lucide-react";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
} from "../components/ui/card";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ZulipServices from "../services/APIs/zulipServices";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverArrow,
} from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { cn } from "../lib/utils";
import { Input } from "../components/ui/input";

const COMPOSE_SEND_MODE_KEY = "civicircle-compose-send-mode";
const SCHEDULED_MESSAGES_KEY = "civicircle-scheduled-messages";

function nextMondayNineAM(from: Date): Date {
  const start = new Date(from);
  start.setSeconds(0, 0);
  for (let add = 0; add <= 14; add++) {
    const d = new Date(start);
    d.setDate(start.getDate() + add);
    d.setHours(9, 0, 0, 0);
    if (d.getDay() === 1 && d.getTime() > from.getTime()) return d;
  }
  const d = new Date(start);
  const day = d.getDay();
  const add = day === 0 ? 1 : (8 - day) % 7 || 7;
  d.setDate(d.getDate() + add);
  d.setHours(9, 0, 0, 0);
  return d;
}

function buildScheduleSlots(reference = new Date()) {
  const fmt = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  const today4 = new Date(reference);
  today4.setHours(16, 0, 0, 0);

  const tomorrow9 = new Date(reference);
  tomorrow9.setDate(tomorrow9.getDate() + 1);
  tomorrow9.setHours(9, 0, 0, 0);

  const tomorrow4 = new Date(reference);
  tomorrow4.setDate(tomorrow4.getDate() + 1);
  tomorrow4.setHours(16, 0, 0, 0);

  const mon9 = nextMondayNineAM(reference);
  const monLabel = `${mon9.toLocaleDateString(undefined, { weekday: "long" })} at ${fmt(mon9)}`;

  return [
    { label: `Today at ${fmt(today4)}`, at: today4 },
    { label: `Tomorrow at ${fmt(tomorrow9)}`, at: tomorrow9 },
    { label: `Tomorrow at ${fmt(tomorrow4)}`, at: tomorrow4 },
    { label: monLabel, at: mon9 },
  ];
}

function toDatetimeLocalValue(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

function ScheduleMessagePanel({
  slots,
  onPick,
  onCustom,
  useDropdownItems,
}: {
  slots: ReturnType<typeof buildScheduleSlots>;
  onPick: (at: Date) => void;
  onCustom: () => void;
  useDropdownItems: boolean;
}) {
  const rowClass =
    "w-full cursor-pointer px-3 py-2.5 text-left text-sm text-foreground transition-colors focus-visible:outline-none";

  const Row = ({ label, onActivate }: { label: string; onActivate: () => void }) =>
    useDropdownItems ? (
      <DropdownMenuItem
        className={cn(rowClass, "rounded-none focus:bg-accent")}
        onSelect={() => onActivate()}
      >
        {label}
      </DropdownMenuItem>
    ) : (
      <button
        type="button"
        className={cn(rowClass, "rounded-none hover:bg-accent")}
        onClick={onActivate}
      >
        {label}
      </button>
    );

  const Sep = () => <div className="h-px w-full bg-border" role="separator" />;

  return (
    <div className="w-[min(17rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
      <div className="border-b border-border px-3 py-2.5">
        <p className="text-sm font-semibold text-foreground">Schedule message</p>
      </div>
      <Row label={slots[0].label} onActivate={() => onPick(slots[0].at)} />
      <Sep />
      <Row label={slots[1].label} onActivate={() => onPick(slots[1].at)} />
      <Row label={slots[2].label} onActivate={() => onPick(slots[2].at)} />
      <Sep />
      <Row label={slots[3].label} onActivate={() => onPick(slots[3].at)} />
      <Sep />
      {useDropdownItems ? (
        <DropdownMenuItem
          className={cn(rowClass, "rounded-none focus:bg-accent")}
          onSelect={() => onCustom()}
        >
          Custom time
        </DropdownMenuItem>
      ) : (
        <button
          type="button"
          className={cn(rowClass, "rounded-none hover:bg-accent")}
          onClick={onCustom}
        >
          Custom time
        </button>
      )}
    </div>
  );
}

type ComposeSendMode = "enter-send" | "ctrl-enter-send";

function ComposerKbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="mx-0.5 inline-flex min-h-[1.125rem] min-w-[1.25rem] items-center justify-center rounded border border-primary/40 bg-background px-1 py-0.5 font-sans text-[10px] font-semibold leading-none text-primary shadow-sm">
      {children}
    </kbd>
  );
}

const apiBaseUrl = import.meta.env.VITE_CITYCOLLAB_API_ROOT_URL;

// Helper function to format Zulip message content
const formatMessageContent = (html: any) => {
  if (!html) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Get auth credentials from token
  const authToken = localStorage.getItem("zulipToken");
  let authParams = "";
  if (authToken && authToken.startsWith("Basic ")) {
    try {
      const decoded = atob(authToken.replace("Basic ", ""));
      const [email, apiKey] = decoded.split(":");
      authParams = `api_key=${apiKey}&email=${encodeURIComponent(email)}`;
    } catch (e) {
      console.error("Error decoding zulipToken", e);
    }
  }

  // Image file extensions — render as preview
  const IMAGE_EXTS = /\.(png|jpe?g|gif|webp|svg)$/i;
  // Non-image file extensions — render as a download link
  const FILE_EXTS = /\.(pdf|docx?|xlsx?|csv|txt|pptx?|zip|rar|7z|mp4|mov|avi|mp3|wav)$/i;

  // Fix relative URLs for user uploads
  const links = tempDiv.querySelectorAll("a");
  links.forEach(link => {
    const href = link.getAttribute("href");
    if (href && href.startsWith("/")) {
      const fullUrl = `${apiBaseUrl}${href}`;
      const separator = fullUrl.includes("?") ? "&" : "?";
      link.setAttribute("href", authParams ? `${fullUrl}${separator}${authParams}` : fullUrl);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    }

    // Handle standalone file-name links that Zulip renders above image previews.
    const linkText = link.textContent?.trim() ?? "";
    const isUploadLink =
      href?.includes("/user_uploads/") || href?.startsWith("/");

    if (!isUploadLink) return;

    const containsImg = link.querySelector("img") !== null;
    if (containsImg) {
      // Anchor wraps an image — unwrap so the <img> still renders as preview
      while (link.firstChild) {
        link.parentNode?.insertBefore(link.firstChild, link);
      }
      link.remove();
    } else if (IMAGE_EXTS.test(linkText)) {
      // Plain image filename link with no <img> child — remove (image block handles it)
      link.remove();
    } else if (FILE_EXTS.test(linkText)) {
      // Non-image file — style it as a download link chip
      link.style.cssText = [
        "display:inline-flex",
        "align-items:center",
        "gap:6px",
        "padding:4px 10px",
        "border-radius:6px",
        "border:1px solid currentColor",
        "opacity:0.75",
        "font-size:0.8rem",
        "font-weight:500",
        "text-decoration:none",
        "word-break:break-all",
      ].join(";");
      // Prepend a 📎 icon if not already there
      if (!link.querySelector(".file-link-icon")) {
        const icon = document.createElement("span");
        icon.className = "file-link-icon";
        icon.textContent = "📎";
        link.prepend(icon);
      }
    }
  });

  const images = tempDiv.querySelectorAll("img");
  images.forEach(img => {
    const src = img.getAttribute("src");
    if (src && (src.startsWith("/") || src.includes("/user_uploads/"))) {
      const fullUrl = src.startsWith("/") ? `${apiBaseUrl}${src}` : src;
      const separator = fullUrl.includes("?") ? "&" : "?";
      img.setAttribute("src", authParams ? `${fullUrl}${separator}${authParams}` : fullUrl);
    }
  });

  return tempDiv.innerHTML;
};

// Common Zulip emoji mapping (Expanded for better variety)
const emojiMap: Record<string, string> = {
  // Popular/Smileys
  smile: "😊",
  laughing: "😆",
  wink: "😉",
  heart_eyes: "😍",
  sweat_smile: "😅",
  joy: "😂",
  // People/Emotions
  thumbs_up: "👍",
  thumbs_down: "👎",
  ok_hand: "👌",
  pray: "🙏",
  heart: "❤️",
  broken_heart: "💔",
  // Nature/Objects
  fire: "🔥",
  rocket: "🚀",
  tada: "🎉",
  party_popper: "🥳",
  100: "💯",
  sparkles: "✨",
  star: "⭐",
  eyes: "👀",
  octopus: "🐙",
  checkmark: "✅",
  hammer_and_wrench: "🛠️",
  construction: "🏗️",
};

const emojiCategories = [
  { label: "Popular", emojis: ["smile", "laughing", "heart", "thumbs_up", "tada", "fire", "octopus", "hammer_and_wrench"] },
  { label: "Smileys & Emotion", emojis: ["wink", "heart_eyes", "sweat_smile", "joy", "broken_heart", "eyes"] },
  { label: "Hands & Body", emojis: ["thumbs_down", "ok_hand", "pray", "checkmark", "100"] },
  { label: "Objects & More", emojis: ["rocket", "party_popper", "sparkles", "star", "construction"] },
];

const emojiGlyph = (text: string) => (
  <span className="emoji-font inline-flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center text-[1.125rem]">
    {text}
  </span>
);

const getEmojiDisplay = (reaction: any, customEmojis: Record<string, any> = {}) => {
  if (reaction.reaction_type === "unicode_emoji") {
    try {
      return emojiGlyph(String.fromCodePoint(parseInt(reaction.emoji_code, 16)));
    } catch {
      return emojiGlyph(emojiMap[reaction.emoji_name] || `:${reaction.emoji_name}:`);
    }
  }
  if (reaction.reaction_type === "realm_emoji" || reaction.reaction_type === "zulip_extra_emoji") {
    const custom = customEmojis[reaction.emoji_name] || customEmojis[reaction.emoji_code];
    if (custom) {
      return <img src={custom.source_url} alt={reaction.emoji_name} className="h-4 w-4 object-contain" />;
    }
  }
  return emojiGlyph(emojiMap[reaction.emoji_name] || `:${reaction.emoji_name}:`);
};

// Helper function to format message time
const formatMessageTime = (timestamp: any) =>
  new Date(timestamp * 1000).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

// Helper function to format message date
const formatMessageDate = (timestamp: any) => {
  const messageDate = new Date(timestamp * 1000);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isToday =
    messageDate.getDate() === today.getDate() &&
    messageDate.getMonth() === today.getMonth() &&
    messageDate.getFullYear() === today.getFullYear();

  const isYesterday =
    messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear();

  if (isToday) {
    return "Today";
  }
  if (isYesterday) {
    return "Yesterday";
  }

  return messageDate.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const ConversationDetail = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const dmId = searchParams.get("dmId");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // Holds the uploaded file info until user clicks Send
  const [attachedFile, setAttachedFile] = useState<{ name: string; uri: string } | null>(null);

  // Fetch custom emojis
  const { data: customEmojisData } = useQuery({
    queryKey: ["customEmojis"],
    queryFn: () => ZulipServices.getCustomEmojis(),
  });

  const customEmojis = customEmojisData?.emoji || {};
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [composeSendMode, setComposeSendMode] = useState<ComposeSendMode>(() => {
    const stored = localStorage.getItem(COMPOSE_SEND_MODE_KEY);
    if (stored === "enter-send" || stored === "ctrl-enter-send") return stored;
    return "ctrl-enter-send";
  });

  useEffect(() => {
    localStorage.setItem(COMPOSE_SEND_MODE_KEY, composeSendMode);
  }, [composeSendMode]);

  const insertNewlineAtCursor = () => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    setMessage(prev => `${prev.slice(0, start)}\n${prev.slice(end)}`);
    const pos = start + 1;
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = pos;
      el.focus();
    });
  };

  const currentUserId = parseInt(
    localStorage.getItem("zulipUserId") ?? "51",
    10
  );

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [messageToDeleteId, setMessageToDeleteId] = useState<number | null>(null);

  const scheduleSlots = useMemo(() => buildScheduleSlots(), []);
  const [customScheduleOpen, setCustomScheduleOpen] = useState(false);
  const [customScheduleAt, setCustomScheduleAt] = useState("");

  const openCustomScheduleDialog = () => {
    const d = new Date();
    d.setHours(d.getHours() + 1, d.getMinutes(), 0, 0);
    setCustomScheduleAt(toDatetimeLocalValue(d));
    setCustomScheduleOpen(true);
  };

  // Fetch users data
  const { data: usersData } = useQuery({
    queryKey: ["/users"],
    queryFn: () => ZulipServices.getUsers(),
  });

  // Fetch DM messages
  const payload = useMemo(
    () => ({
      anchor: "newest",
      num_before: 100,
      num_after: 0,
      client_gravatar: true,
      allow_empty_topic_name: true,
      narrow: JSON.stringify([
        { negated: false, operator: "dm", operand: [Number(dmId)] },
      ]),
    }),
    [dmId]
  );

  const {
    data: DMChatData,
    refetch: refetchDMChatData,
    isLoading: isLoadingMessages,
  } = useQuery({
    queryKey: ["messages", payload],
    queryFn: () => ZulipServices.getDirectMessage({ payload }),
    enabled: !!dmId,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (DMChatData?.messages) {
      // Sort messages by timestamp (oldest first for display)
      const sortedMessages = [...DMChatData.messages].sort(
        (a: any, b: any) => a.timestamp - b.timestamp
      );
      setMessages(sortedMessages);
    }
  }, [DMChatData]);

  // Get conversation user info
  const conversation = useMemo(() => {
    if (!dmId) {
      return {
        id: id ?? "1",
        name: "Loading...",
        avatar: null,
        status: "offline",
      };
    }

    // Try to get user info from messages first
    if (messages.length > 0) {
      const firstMessage = messages[0];
      if (
        firstMessage?.display_recipient &&
        Array.isArray(firstMessage.display_recipient)
      ) {
        const otherUser = firstMessage.display_recipient.find(
          (u: any) => u.id !== currentUserId
        );
        if (otherUser) {
          const user = usersData?.members?.find(
            (u: any) => u.user_id === otherUser.id
          );
          return {
            id: otherUser.id,
            name: otherUser.full_name ?? user?.full_name ?? "Unknown User",
            avatar: otherUser.avatar_url?.startsWith("http")
              ? otherUser.avatar_url
              : otherUser.avatar_url
                ? `${apiBaseUrl}${otherUser.avatar_url}`
                : user?.avatar_url?.startsWith("http")
                  ? user.avatar_url
                  : user?.avatar_url
                    ? `${apiBaseUrl}${user.avatar_url}`
                    : null,
            status: "online",
          };
        }
      }
    }

    // Fallback: try to get from usersData by dmId
    if (usersData?.members) {
      const user = usersData.members.find(
        (u: any) => u.user_id === Number(dmId)
      );
      if (user) {
        return {
          id: user.user_id,
          name: user.full_name ?? "Unknown User",
          avatar: user.avatar_url?.startsWith("http")
            ? user.avatar_url
            : user.avatar_url
              ? `${apiBaseUrl}${user.avatar_url}`
              : null,
          status: "online",
        };
      }
    }

    return {
      id: dmId,
      name: "Loading...",
      avatar: null,
      status: "offline",
    };
  }, [messages, usersData, currentUserId, id, dmId]);

  // Group messages by date
  const groupedMessages = useMemo(() => {
    return messages?.reduce((groups: any, msg: any) => {
      const dateLabel = formatMessageDate(msg.timestamp);
      groups[dateLabel] ??= [];
      groups[dateLabel].push(msg);
      return groups;
    }, {});
  }, [messages]);
  const handleDeleteMessage = (messageId: number) => {
    setMessageToDeleteId(messageId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteMessage = async () => {
    if (messageToDeleteId === null) return;
    try {
      await ZulipServices.deleteMessage(messageToDeleteId);
      refetchDMChatData?.();
    } catch (error) {
      console.error("Error deleting message:", error);
    } finally {
      setIsDeleteDialogOpen(false);
      setMessageToDeleteId(null);
    }
  };

  const handleToggleReaction = async (messageId: number, emojiName: string, hasReacted: boolean) => {
    try {
      if (hasReacted) {
        await ZulipServices.removeReaction(messageId, emojiName);
      } else {
        await ZulipServices.addReaction(messageId, emojiName);
      }
      refetchDMChatData?.();
    } catch (error) {
      console.error("Error toggling reaction:", error);
    }
  };

  const handleSendMessage = async () => {
    // Need either text message or an attached file
    if (!message.trim() && !attachedFile) return;
    if (!dmId) return;

    try {
      // Build content: text + file link if attached
      let content = message.trim();
      if (attachedFile) {
        const fileLink = `[${attachedFile.name}](${attachedFile.uri})`;
        content = content ? `${content}\n${fileLink}` : fileLink;
      }

      await ZulipServices.sendDirectMessage({
        to: [Number(dmId)],
        type: "private",
        content,
      });
      setMessage("");
      setAttachedFile(null);
      refetchDMChatData?.();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // Upload file and store result — does NOT send immediately
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-selected
    e.target.value = "";

    setIsUploading(true);
    try {
      const uploadRes = await ZulipServices.uploadFile(file);
      const uri: string | undefined = uploadRes?.uri;
      const filename: string = uploadRes?.filename ?? file.name;
      if (!uri) throw new Error("Upload failed: no URI returned");

      // Store for send step — show chip in composer
      setAttachedFile({ name: filename, uri });
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const scheduleMessageAt = (at: Date): boolean => {
    const text = message.trim();
    if (!text && !attachedFile) {
      toast({
        title: "Nothing to schedule",
        description: "Add text or an attachment first.",
      });
      return false;
    }
    if (at.getTime() <= Date.now()) {
      toast({
        title: "Time not valid",
        description: "Choose a future time.",
        variant: "destructive",
      });
      return false;
    }
    try {
      const raw = localStorage.getItem(SCHEDULED_MESSAGES_KEY);
      const parsed = JSON.parse(raw || "[]");
      const list = Array.isArray(parsed) ? parsed : [];
      list.push({
        id: Date.now(),
        dmId: dmId ?? null,
        text: message,
        attachment: attachedFile,
        scheduledAt: at.toISOString(),
      });
      localStorage.setItem(SCHEDULED_MESSAGES_KEY, JSON.stringify(list));
      setMessage("");
      setAttachedFile(null);
      toast({
        title: "Message scheduled",
        description: at.toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        }),
      });
      return true;
    } catch {
      toast({ title: "Could not schedule", variant: "destructive" });
      return false;
    }
  };

  const confirmCustomSchedule = () => {
    const at = new Date(customScheduleAt);
    if (Number.isNaN(at.getTime())) {
      toast({ title: "Invalid date", variant: "destructive" });
      return;
    }
    if (scheduleMessageAt(at)) setCustomScheduleOpen(false);
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <header className="sticky top-0 z-50 shrink-0 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
                className="shrink-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarImage src={conversation.avatar ?? undefined} />
                <AvatarFallback>
                  {conversation.name
                    .split(" ")
                    .map((n: string) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <h1 className="truncate text-lg font-bold">
                  {conversation.name}
                </h1>
                <p className="text-xs text-muted-foreground">
                  {conversation.status}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="ghost" size="icon">
                <Phone className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon">
                <Video className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <ScrollArea className="flex-1 overflow-y-auto">
        <div className="container mx-auto max-w-4xl px-4 py-4">
          <div className="space-y-4">
            {isLoadingMessages ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Loading messages...
              </div>
            ) : Object.keys(groupedMessages ?? {}).length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No messages yet. Start the conversation!
              </div>
            ) : (
              Object.entries(groupedMessages ?? {}).map(
                ([dateLabel, msgs]: any) => (
                  <div key={dateLabel}>
                    {/* Date Header */}
                    <div className="sticky top-0 z-10 mx-4 my-2 text-center">
                      <span className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm">
                        {dateLabel}
                      </span>
                    </div>

                    {/* Render messages */}
                    {msgs.map((m: any) => {
                      const isSent = m.sender_id === currentUserId;
                      const user =
                        usersData?.members?.find(
                          (u: any) => u.user_id === m.sender_id
                        ) ?? null;

                      const avatarUrl = user?.avatar_url?.startsWith("http")
                        ? user.avatar_url
                        : user?.avatar_url
                          ? `${apiBaseUrl}${user.avatar_url}`
                          : null;

                      return (
                        <div
                          key={m.id}
                          className={`mt-3 flex gap-3 ${isSent ? "flex-row-reverse" : "flex-row"}`}
                        >
                          {!isSent && (
                            <Avatar className="h-10 w-10 shrink-0">
                              <AvatarImage src={avatarUrl ?? undefined} />
                              <AvatarFallback>
                                {user?.full_name
                                  ?.split(" ")
                                  .map((n: string) => n[0])
                                  .join("")
                                  .slice(0, 2)
                                  .toUpperCase() ?? "U"}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <div
                            className={`max-w-md flex-1 ${isSent ? "" : ""} flex flex-col`}
                          >
                            {!isSent && (
                              <span className="mb-1 px-1 text-xs font-semibold">
                                {user?.full_name ?? "Unknown User"}
                              </span>
                            )}
                            <Card
                              className={`group ${isSent ? "bg-primary text-primary-foreground" : "bg-card"}`}
                            >
                              <CardContent className="p-3">
                                <div
                                  className="zulip-message-content text-sm"
                                  dangerouslySetInnerHTML={{
                                    __html: formatMessageContent(m.content),
                                  }}
                                />

                                {/* Reactions Row */}
                                {m.reactions && m.reactions.length > 0 && (
                                  <div
                                    className={`mt-3 flex flex-wrap items-center gap-1.5 border-t pt-2 ${isSent ? "border-primary-foreground/10" : "border-border"}`}
                                  >
                                    {Object.values(
                                      m.reactions.reduce((acc: any, r: any) => {
                                        const key = r.emoji_name;
                                        if (!acc[key]) {
                                          acc[key] = { ...r, count: 0, hasReacted: false };
                                        }
                                        acc[key].count++;
                                        if (r.user_id === currentUserId) acc[key].hasReacted = true;
                                        return acc;
                                      }, {})
                                    ).map((reaction: any) => (
                                        <Badge
                                          key={`${m.id}-${reaction.emoji_name}`}
                                          variant="outline"
                                          className={`flex h-7 cursor-pointer items-center gap-1.5 rounded-full px-2 transition-colors ${
                                            isSent
                                              ? reaction.hasReacted
                                                ? "border-primary-foreground/40 bg-primary-foreground/20 text-primary-foreground"
                                                : "border-primary-foreground/20 bg-primary-foreground/5 text-primary-foreground/70 hover:bg-primary-foreground/10"
                                              : reaction.hasReacted
                                                ? "border-primary bg-primary/10 text-primary"
                                                : "border-border text-muted-foreground hover:bg-muted"
                                          }`}
                                          onClick={() => handleToggleReaction(m.id, reaction.emoji_name, reaction.hasReacted)}
                                        >
                                          <span className="inline-flex shrink-0 items-center justify-center">
                                            {getEmojiDisplay(reaction, customEmojis)}
                                          </span>
                                          {reaction.hasReacted && (
                                            <span className="text-[10px] font-semibold">You</span>
                                          )}
                                        {(!reaction.hasReacted || reaction.count > 1) && (
                                          <span className="text-[11px] font-medium opacity-70">
                                            {reaction.hasReacted ? reaction.count - 1 || "" : reaction.count}
                                          </span>
                                        )}
                                      </Badge>
                                    ))}
                                  </div>
                                )}

                                <div className="mt-2 flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`text-xs ${isSent ? "text-primary-foreground/70" : "text-muted-foreground"}`}
                                    >
                                      {formatMessageTime(m.timestamp)}
                                    </span>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className={`h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity ${isSent ? "text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                                        >
                                          <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align={isSent ? "end" : "start"} className="w-48">
                                        <DropdownMenuItem>
                                          <Smile className="mr-2 h-4 w-4" />
                                          <span>Add emoji</span>
                                        </DropdownMenuItem>
                                        {isSent && (
                                          <DropdownMenuItem>
                                            <Edit3 className="mr-2 h-4 w-4" />
                                            <span>Edit message</span>
                                          </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem>
                                          <Quote className="mr-2 h-4 w-4" />
                                          <span>Quote message</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem>
                                          <Clock className="mr-2 h-4 w-4" />
                                          <span>Remind me</span>
                                        </DropdownMenuItem>
                                        {isSent && <DropdownMenuSeparator />}
                                        {isSent && (
                                          <DropdownMenuItem
                                            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                                            onClick={() => handleDeleteMessage(m.id)}
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            <span>Delete message</span>
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>

                                  {/* Add reaction picker */}
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className={`h-6 w-6 rounded-full transition-opacity ${m.reactions?.length ? "opacity-100" : "opacity-0 group-hover:opacity-100"} ${isSent ? "text-primary-foreground/50 hover:bg-primary-foreground/10 hover:text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                                      >
                                        <Smile className="h-3.5 w-3.5" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-1" side="top" align={isSent ? "end" : "start"}>
                                      <div className="flex flex-col max-h-80 overflow-y-auto">
                                        {/* Standard Emojis */}
                                        {emojiCategories.map((category) => (
                                          <div key={category.label} className="p-2 pt-1">
                                            <div className="text-[10px] font-bold text-muted-foreground uppercase mb-1 px-1">
                                              {category.label}
                                            </div>
                                            <div className="flex flex-wrap gap-0.5">
                                              {category.emojis.map((name) => (
                                                <Button
                                                  key={name}
                                                  variant="ghost"
                                                  size="icon"
                                                  className={`emoji-font h-8 w-8 text-lg ${m.reactions?.some((r: any) => r.emoji_name === name && r.user_id === currentUserId) ? "bg-primary/10" : ""}`}
                                                  onClick={() => {
                                                    const hasReacted = m.reactions?.some((r: any) => r.emoji_name === name && r.user_id === currentUserId);
                                                    handleToggleReaction(m.id, name, hasReacted);
                                                  }}
                                                >
                                                  {emojiMap[name]}
                                                </Button>
                                              ))}
                                            </div>
                                          </div>
                                        ))}

                                        {/* Custom Realm Emojis */}
                                        {Object.keys(customEmojis).length > 0 && (
                                          <div className="p-2 pt-1">
                                            <div className="text-[10px] font-bold text-muted-foreground uppercase mb-1 px-1">
                                              Custom
                                            </div>
                                            <div className="flex flex-wrap gap-0.5">
                                              {Object.values(customEmojis).map((emoji: any) => (
                                                <Button
                                                  key={emoji.name}
                                                  variant="ghost"
                                                  size="icon"
                                                  className={`h-8 w-8 p-1.5 ${m.reactions?.some((r: any) => r.emoji_name === emoji.name && r.user_id === currentUserId) ? "bg-primary/10" : ""}`}
                                                  onClick={() => {
                                                    const hasReacted = m.reactions?.some((r: any) => r.emoji_name === emoji.name && r.user_id === currentUserId);
                                                    handleToggleReaction(m.id, emoji.name, hasReacted);
                                                  }}
                                                >
                                                  <img src={emoji.source_url} alt={emoji.name} className="h-full w-full object-contain" title={emoji.name} />
                                                </Button>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                          {isSent && (
                            <Avatar className="h-10 w-10 shrink-0">
                              <AvatarImage src={avatarUrl ?? undefined} />
                              <AvatarFallback>
                                {user?.full_name
                                  ?.split(" ")
                                  .map((n: string) => n[0])
                                  .join("")
                                  .slice(0, 2)
                                  .toUpperCase() ?? "U"}
                              </AvatarFallback>
                            </Avatar>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )
            )}
          </div>
        </div>
      </ScrollArea>

      {/* Rich Composer — matches project design system */}
      <div className="shrink-0 border-t border-border bg-card">
        <div className="mx-auto max-w-4xl px-4 py-3">
          <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">

            {/* Compose textarea */}
            <textarea
              ref={textareaRef}
              rows={2}
              placeholder="Compose your message here"
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => {
                if (e.key !== "Enter") return;

                if (composeSendMode === "ctrl-enter-send") {
                  if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                  return;
                }

                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  insertNewlineAtCursor();
                  return;
                }
                if (e.shiftKey) return;
                e.preventDefault();
                handleSendMessage();
              }}
              className="composer-textarea"
            />

            {/* Attached file chip preview */}
            {attachedFile && (
              <div className="flex items-center gap-2 border-t border-border px-3 py-1.5">
                <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-muted px-2.5 py-1.5">
                  <FileIcon className="h-4 w-4 shrink-0 text-primary" />
                  <span className="flex-1 truncate text-xs font-medium text-foreground">
                    {attachedFile.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setAttachedFile(null)}
                    className="ml-1 rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    title="Remove attachment"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              disabled={isUploading}
            />

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-0.5 border-t border-border px-2 py-1">

              {/* Left icon groups */}
              <div className="flex flex-1 flex-wrap items-center gap-0.5">

                {/* Group 1 — media / attach */}
                <Button
                  variant="ghost" size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  title="Preview"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost" size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  title="Attach file"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploading
                    ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
                    : <Paperclip className="h-4 w-4" />
                  }
                </Button>
                <Button
                  variant="ghost" size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  title="Video call"
                >
                  <Video className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost" size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  title="Phone"
                >
                  <Phone className="h-4 w-4" />
                </Button>

                <span className="mx-1 h-4 w-px bg-border" />

                {/* Group 2 — emoji / schedule / mention */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  title="Emoji"
                  type="button"
                >
                  <Smile className="h-4 w-4" />
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      title="Schedule message"
                      type="button"
                    >
                      <Clock className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="top"
                    align="center"
                    sideOffset={10}
                    className="w-auto border-0 bg-transparent p-0 shadow-none"
                  >
                    <ScheduleMessagePanel
                      slots={scheduleSlots}
                      useDropdownItems={false}
                      onPick={scheduleMessageAt}
                      onCustom={openCustomScheduleDialog}
                    />
                    <PopoverArrow className="fill-popover stroke-border" />
                  </PopoverContent>
                </Popover>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  title="Mention"
                  type="button"
                >
                  <MessageSquare className="h-4 w-4" />
                </Button>

                <span className="mx-1 h-4 w-px bg-border" />

                {/* Group 3 — text formatting */}
                {[
                  { icon: <Link2 className="h-4 w-4" />, title: "Link" },
                  { icon: <Bold className="h-4 w-4" />, title: "Bold" },
                  { icon: <Italic className="h-4 w-4" />, title: "Italic" },
                  { icon: <Strikethrough className="h-4 w-4" />, title: "Strikethrough" },
                ].map((btn, i) => (
                  <Button key={i} variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title={btn.title}>
                    {btn.icon}
                  </Button>
                ))}

                <span className="mx-1 h-4 w-px bg-border" />

                {/* Group 4 — lists / blocks */}
                {[
                  { icon: <ListOrdered className="h-4 w-4" />, title: "Numbered list" },
                  { icon: <List className="h-4 w-4" />, title: "Bullet list" },
                  { icon: <Quote className="h-4 w-4" />, title: "Quote" },
                  { icon: <Table2 className="h-4 w-4" />, title: "Table" },
                  { icon: <Code2 className="h-4 w-4" />, title: "Code block" },
                  { icon: <BarChart2 className="h-4 w-4" />, title: "Math" },
                  { icon: <CheckSquare className="h-4 w-4" />, title: "Checklist" },
                  { icon: <HelpCircle className="h-4 w-4" />, title: "Help" },
                ].map((btn, i) => (
                  <Button key={i} variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title={btn.title}>
                    {btn.icon}
                  </Button>
                ))}
              </div>

              {/* Right side — Send + More */}
              <div className="ml-auto flex items-center gap-1.5">
                <Button
                  size="sm"
                  onClick={handleSendMessage}
                  disabled={!message.trim() && !attachedFile}
                  className="h-8 px-3"
                  title="Send message"
                >
                  <Send className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      title="More options"
                      type="button"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    side="top"
                    sideOffset={8}
                    className="w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-border bg-popover p-0 shadow-lg"
                  >
                    <DropdownMenuRadioGroup
                      value={composeSendMode}
                      onValueChange={(v: string) => {
                        if (v === "enter-send" || v === "ctrl-enter-send") setComposeSendMode(v);
                      }}
                    >
                      <div className="space-y-0.5 px-2 py-2">
                        <DropdownMenuRadioItem
                          value="enter-send"
                          className="cursor-pointer items-start rounded-lg py-2.5 pl-8 pr-3 focus:bg-accent"
                        >
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium leading-snug text-foreground">
                              Press <ComposerKbd>Enter</ComposerKbd> to send
                            </span>
                            <span className="text-xs leading-snug text-muted-foreground">
                              Press <ComposerKbd>Ctrl</ComposerKbd>
                              <ComposerKbd>Enter</ComposerKbd> to add a new line.
                            </span>
                          </div>
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem
                          value="ctrl-enter-send"
                          className="cursor-pointer items-start rounded-lg py-2.5 pl-8 pr-3 focus:bg-accent"
                        >
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium leading-snug text-foreground">
                              Press <ComposerKbd>Ctrl</ComposerKbd>
                              <ComposerKbd>Enter</ComposerKbd> to send
                            </span>
                            <span className="text-xs leading-snug text-muted-foreground">
                              Press <ComposerKbd>Enter</ComposerKbd> to add a new line.
                            </span>
                          </div>
                        </DropdownMenuRadioItem>
                      </div>
                    </DropdownMenuRadioGroup>
                    <DropdownMenuSeparator className="my-0 bg-border" />
                    <div className="space-y-0.5 px-1 py-1">
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2">
                          <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="text-sm">Schedule message</span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent
                          sideOffset={8}
                          className="border-0 bg-transparent p-0 shadow-none"
                          alignOffset={-2}
                        >
                          <ScheduleMessagePanel
                            slots={scheduleSlots}
                            useDropdownItems
                            onPick={scheduleMessageAt}
                            onCustom={openCustomScheduleDialog}
                          />
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuItem
                        className="cursor-pointer gap-2 rounded-md px-3 py-2"
                        onSelect={() => {
                          try {
                            const raw = localStorage.getItem(SCHEDULED_MESSAGES_KEY);
                            const list = raw ? JSON.parse(raw) : [];
                            const n = Array.isArray(list) ? list.length : 0;
                            toast({
                              title: "Scheduled messages",
                              description:
                                n === 0
                                  ? "You have no scheduled messages."
                                  : `${n} scheduled message${n === 1 ? "" : "s"} (viewer coming soon).`,
                            });
                          } catch {
                            toast({ title: "Scheduled messages", description: "Could not read storage." });
                          }
                        }}
                      >
                        <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="text-sm">View scheduled messages</span>
                      </DropdownMenuItem>
                    </div>
                    <DropdownMenuSeparator className="my-0 bg-border" />
                    <div className="h-1" />
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

            </div>
          </div>
        </div>
      </div>

      <Dialog open={customScheduleOpen} onOpenChange={setCustomScheduleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Custom time</DialogTitle>
          </DialogHeader>
          <Input
            type="datetime-local"
            value={customScheduleAt}
            onChange={e => setCustomScheduleAt(e.target.value)}
            className="w-full"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setCustomScheduleOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmCustomSchedule}>
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolute sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              message from the conversation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMessage}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConversationDetail;
